/**
 * FinanceReportFlow — sections 2–4 of the reports one-pager for the
 * "דוח כספים לייצוא" report type:
 *
 *   2 טווח תאריכים → 3 מה לכלול (statuses / charts / sections) → 4 תצוגה וייצוא
 *
 * Data: transactions in range (+ appointments in range for the treatment-type
 * and therapist join), aggregated client-side by financeReportModel. The same
 * model feeds the recharts preview and the PDF callable (server renders charts
 * as inline SVG).
 *
 * Error codes: CK-3975 (export).
 */

import { useCallback, useMemo, useState } from "react";
import { FilePdf, WarningCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import ContentLoader from "../../../../shared/ContentLoader";
import LoaderLoop from "../../../../shared/loader/LoaderLoop";
import { useGetAppointmentsQuery } from "../../../../../store/services/calendarApi";
import { useGetTransactionsInRangeQuery } from "../../../../../store/services/financeApi";
import { useGenerateFinanceReportPdfMutation } from "../../../../../store/services/patientReportsApi";
import { logActivity, ACTIVITY_ACTIONS, ENTITY_TYPES } from "../../../../../firebase";
import { formatHebrewDate } from "../../../../../utils/dateUtils";
import Section from "../Section";
import PdfReadyCard from "../PdfReadyCard";
import PdfPreviewModal from "../PdfPreviewModal";
import DateRangeStep from "../steps/DateRangeStep";
import FinanceOptionsStep from "./FinanceOptionsStep";
import FinancePreview from "./FinancePreview";
import { buildFinanceReport } from "./financeReportModel";

const REPORT_TYPE_ID = "financeReport";
const REPORT_TYPE_LABEL = "דוח כספים";

function FinanceReportFlow({
  wiz,
  clinicId,
  scopeTherapistId,
  canExportData,
  therapistId,
  therapistName,
  startNum = 2,
  onReset,
}) {
  const {
    startDate,
    endDate,
    rangeOk,
    financeOptions,
    financeStatusesOk,
    financeContentOk,
    financeReady,
  } = wiz;

  const nums = { dates: startNum, options: startNum + 1, preview: startNum + 2 };
  const optionsEnabled = rangeOk;
  const previewEnabled = financeReady;
  const dataGate = !clinicId || !previewEnabled;

  const {
    data: transactions,
    isLoading: txLoading,
    error: txError,
  } = useGetTransactionsInRangeQuery(
    {
      clinicId,
      start: `${startDate}T00:00:00`,
      end: `${endDate}T23:59:59`,
      therapistId: scopeTherapistId || undefined,
    },
    { skip: dataGate }
  );

  const {
    data: appointments,
    isLoading: aptLoading,
    error: aptError,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: `${startDate}T00:00:00`,
      endDate: `${endDate}T23:59:59`,
      therapistId: scopeTherapistId || undefined,
    },
    { skip: dataGate }
  );

  const model = useMemo(() => {
    if (!transactions) return null;
    return buildFinanceReport({
      transactions,
      appointments: appointments || [],
      startDate,
      endDate,
      statuses: financeOptions.statuses,
      charts: financeOptions.charts,
      sections: financeOptions.sections,
    });
  }, [transactions, appointments, startDate, endDate, financeOptions]);

  // ─── Export ───
  const [generatePdf, { isLoading: isExporting }] = useGenerateFinanceReportPdfMutation();
  const [exportError, setExportError] = useState(null);
  const [lastPdfUrl, setLastPdfUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleExport = useCallback(async () => {
    if (!model) return;
    setExportError(null);
    setLastPdfUrl(null);
    setPreviewOpen(false);
    // Stay in CliniKit while rendering; the in-app preview opens once the
    // URL is ready (no popup → nothing to block).
    try {
      const result = await generatePdf({
        clinicId,
        reportTypeId: REPORT_TYPE_ID,
        reportTypeLabel: REPORT_TYPE_LABEL,
        rangeStart: startDate,
        rangeEnd: endDate,
        scopeTherapistId: scopeTherapistId || null,
        statusLabels: model.statusLabels,
        kpis: model.kpis,
        charts: model.charts,
        tables: model.tables,
        rowCount: model.rowCount,
      }).unwrap();

      setLastPdfUrl(result.pdfUrl);
      setPreviewOpen(true);

      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.GENERATE_PATIENT_REPORT,
        entityType: ENTITY_TYPES.PATIENT,
        entityId: clinicId,
        entityName: REPORT_TYPE_LABEL,
        details: {
          reportTypeId: REPORT_TYPE_ID,
          rangeStart: startDate,
          rangeEnd: endDate,
          rowCount: model.rowCount,
          charts: model.charts.map((c) => c.key),
        },
      });
    } catch (error) {
      console.error("[CK-3975] Error generating finance report PDF:", {
        errorCode: "CK-3975",
        errorMessage: error?.message,
        clinicId,
        rowCount: model.rowCount,
        timestamp: new Date().toISOString(),
      });
      setExportError("אירעה שגיאה בהפקת הדוח. נסו שוב בעוד רגע.");
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.GENERATE_PATIENT_REPORT,
        entityType: ENTITY_TYPES.PATIENT,
        entityId: clinicId,
        entityName: REPORT_TYPE_LABEL,
        success: false,
        errorMessage: error?.message || null,
        details: { reportTypeId: REPORT_TYPE_ID, rangeStart: startDate, rangeEnd: endDate },
      });
    }
  }, [model, generatePdf, clinicId, startDate, endDate, scopeTherapistId, therapistId, therapistName]);

  const rangeSummary =
    rangeOk && startDate && endDate
      ? `${formatHebrewDate(startDate)} – ${formatHebrewDate(endDate)}`
      : null;

  const optionsSummary = (() => {
    const n = financeOptions.statuses.size;
    const c = Object.values(financeOptions.charts).filter(Boolean).length;
    return `${n} סטטוסים · ${c} גרפים`;
  })();

  const isLoadingData = txLoading || aptLoading;
  const dataError = txError || aptError;

  return (
    <>
      <div className="prw-row">
        <Section num={nums.dates} title="טווח תאריכים" done={rangeOk} summary={rangeSummary}>
          <DateRangeStep wiz={wiz} />
        </Section>

        {optionsEnabled && (
          <Section
            num={nums.options}
            title="מה לכלול בדוח"
            done={financeStatusesOk && financeContentOk}
            summary={financeStatusesOk && financeContentOk ? optionsSummary : null}
          >
            <FinanceOptionsStep wiz={wiz} />
            {!financeContentOk && (
              <div className="prw-field-error">יש לבחור לפחות גרף או טבלה אחת</div>
            )}
          </Section>
        )}
      </div>

      {previewEnabled && (
        <Section num={nums.preview} title="תצוגה מקדימה וייצוא" done={false}>
          <div className="prw-step-panel">
            {isLoadingData || !model ? (
              dataError ? (
                <div className="prw-preview-state error">
                  <WarningCircle size={22} />
                  <span>אירעה שגיאה בטעינת הנתונים. נסו שוב בעוד רגע.</span>
                </div>
              ) : (
                <ContentLoader message="טוען נתונים..." />
              )
            ) : (
              <>
                <div className="prw-preview-header">
                  <div className="prw-preview-title">
                    <span className="prw-preview-name">{REPORT_TYPE_LABEL}</span>
                    <span className="prw-preview-meta">
                      {rangeSummary} · {model.rowCount} תנועות
                      {scopeTherapistId ? " · מטפל/ת נבחר/ת" : " · כל הקליניקה"}
                    </span>
                  </div>
                  {canExportData && model.rowCount > 0 && (
                    <button
                      type="button"
                      className="prw-btn primary prw-export-btn"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <LoaderLoop size="xs" />
                      ) : (
                        <FilePdf size={16} weight="fill" />
                      )}
                      {isExporting ? "מפיק דוח..." : "ייצוא PDF"}
                    </button>
                  )}
                </div>

                {exportError && <div className="prw-field-error">{exportError}</div>}

                {model.rowCount === 0 ? (
                  <div className="prw-preview-state">
                    <WarningCircle size={22} />
                    <span>אין תנועות בטווח ובסטטוסים שנבחרו</span>
                  </div>
                ) : (
                  <FinancePreview model={model} />
                )}

                <PdfReadyCard url={lastPdfUrl} onPreview={() => setPreviewOpen(true)} />
              </>
            )}
            <button
              type="button"
              className="prw-btn ghost prw-reset-btn"
              onClick={onReset}
              disabled={isExporting}
              title="התחלת דוח חדש"
            >
              <ArrowCounterClockwise size={15} />
              דוח חדש
            </button>
          </div>
        </Section>
      )}

      {previewOpen && lastPdfUrl && (
        <PdfPreviewModal
          url={lastPdfUrl}
          title={REPORT_TYPE_LABEL}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

export default FinanceReportFlow;
